import { create } from 'zustand';
import type { FinalSong } from '../interfaces/final-song';
import { useUserStore } from './user-store';
import { useGlobalStore } from './global-store';
import { getDefaultSource } from '../shared/playback/playback-source';

// Port of src/app/services/i-frame-audio-player-state.service.ts.
// Angular's `selectedSongSignal` is named `selectedSong` here; the plain
// (non-signal) fields playingAudioElement / songPosition are kept as state
// fields (they were never observed reactively in Angular, only read).
//
// Playlist playback (playSongFromPlaylist): the mini-player normally mirrors
// media owned by the inline result-row components. A playlist song may not be
// in the current results, so here the store hands the Player everything it
// needs to OWN the media itself: for YouTube it just sets playingIframeId (the
// Player builds the iframe in its own offscreen host); for MP3 it builds a
// fresh <audio> element the Player drives directly.
interface PlayerControls {
  play: () => void;
  pause: () => void;
}

interface PlayerState {
  selectedSong: FinalSong | undefined;
  playingIframeId: string | undefined;
  playingAudioUrl: string | undefined;
  songPlayedFromPlaylist: boolean;

  playingAudioElement: HTMLAudioElement | undefined;
  songPosition: number | undefined;

  // Playlist-row feedback + control. The Player registers its play/pause here
  // on mount and mirrors its internal playing state into isPlaying, so the
  // playlist bar can show a per-row playing/paused state and pause/resume the
  // active song without reaching into the Player component.
  isPlaying: boolean;
  controls: PlayerControls | null;

  setPlayingIframe: (youtube_id: string | undefined) => void;
  setPlayingAudio: (url: string | undefined, audioElement: HTMLAudioElement | undefined) => void;
  setSongAndPosition: (song: FinalSong | undefined, position: number | undefined) => void;
  playSongFromPlaylist: (song: FinalSong) => void;
  setIsPlaying: (value: boolean) => void;
  setControls: (controls: PlayerControls | null) => void;
}

export const usePlayerStore = create<PlayerState>()((set) => ({
  selectedSong: undefined,
  playingIframeId: undefined,
  playingAudioUrl: undefined,
  songPlayedFromPlaylist: false,

  playingAudioElement: undefined,
  songPosition: undefined,

  isPlaying: false,
  controls: null,

  setPlayingIframe: (youtube_id) => set({
    playingAudioElement: undefined,
    playingIframeId: youtube_id,
    playingAudioUrl: undefined,
  }),

  setPlayingAudio: (url, audioElement) => set({
    playingAudioElement: audioElement,
    playingIframeId: undefined,
    playingAudioUrl: url,
    songPlayedFromPlaylist: false,
  }),

  setSongAndPosition: (song, position) => set({
    selectedSong: song,
    songPosition: position,
    songPlayedFromPlaylist: false,
  }),

  playSongFromPlaylist: (song) => {
    const isWhiteLabel = useUserStore.getState().isUserWhiteLabel;
    const source = getDefaultSource(song, isWhiteLabel);

    // Keep the results row (if this song is also on screen) highlighted, and
    // clear songPosition so returnToSongLocation doesn't scroll to a stale
    // results offset for a song that isn't in the list.
    useGlobalStore.getState().setGlobalField('highlightedSong', song);

    if (source === 'mp3' && song.url) {
      const audio = new Audio(song.url);
      set({
        selectedSong: song,
        songPlayedFromPlaylist: true,
        playingAudioUrl: song.url,
        playingAudioElement: audio,
        playingIframeId: undefined,
        songPosition: undefined,
      });
      return;
    }

    set({
      selectedSong: song,
      songPlayedFromPlaylist: true,
      playingIframeId: song.youtube_id,
      playingAudioUrl: undefined,
      playingAudioElement: undefined,
      songPosition: undefined,
    });
  },

  setIsPlaying: (value) => set({ isPlaying: value }),
  setControls: (controls) => set({ controls }),
}));
